'use client';

import { useEffect, useRef, useState } from 'react';
import { OpenFeature, OpenFeatureProvider } from '@openfeature/react-sdk';
import { LaunchDarklyClientProvider } from '@openfeature/launchdarkly-client-provider';
import { FeatureFlagContext, FeatureFlagProviderProps } from './types';
import {
  buildProviderOptions,
  createProviderLogger,
  registerErrorHook,
  subscribeProviderEvents,
  toEvaluationContext,
} from './utils';
import { isEqual } from 'lodash';
import type { FeatureFlagError } from './errors';
import { RenderMode, useRenderMode } from '@upms/use-render-mode';

const DEFAULT_LD_INITIALIZATION_TIMEOUT = 5;

export function FeatureFlagProvider({
  launchDarklyConfig,
  context,
  defaultEvaluationOptions,
  onError,
  children,
}: FeatureFlagProviderProps) {
  const onErrorRef = useRef<((error: FeatureFlagError) => void) | undefined>(undefined);
  onErrorRef.current = onError;

  const contextRef = useRef<FeatureFlagContext | undefined>(undefined);

  // Must run synchronously — useEffect fires after OpenFeatureProvider renders,
  // which crashes when no provider is set yet.
  const initializedRef = useRef(false);
  const [isProviderInitialized, setIsProviderInitialized] = useState<boolean>(false);

  const renderMode = useRenderMode();

  // only initialize the provider on the client side
  if (renderMode === RenderMode.client && !initializedRef.current) {
    initializedRef.current = true;
    if (context) {
      contextRef.current = context;
      OpenFeature.setContext(toEvaluationContext(context));
    }
    // initilize the provider
    const { clientKey, ...providerOptions } = launchDarklyConfig;

    OpenFeature.setProviderAndWait(
      new LaunchDarklyClientProvider(clientKey, {
        streaming: true,
        initializationTimeout: DEFAULT_LD_INITIALIZATION_TIMEOUT,
        ...providerOptions,
        logger: createProviderLogger(onErrorRef),
      })
    ).then(() => {
      setIsProviderInitialized(true);
    });
  }

  useEffect(() => {
    if (renderMode === RenderMode.server || !isProviderInitialized) return;
    subscribeProviderEvents(onErrorRef);
    registerErrorHook(onErrorRef);

    return () => {
      OpenFeature.clearHandlers();
      OpenFeature.clearHooks();
    };
  }, [renderMode, isProviderInitialized]);

  useEffect(() => {
    if (renderMode === RenderMode.server || !isProviderInitialized) return;
    // update context if it has changed between renders
    if (context && !isEqual(context, contextRef.current)) {
      OpenFeature.setContext(toEvaluationContext(context));
      contextRef.current = context;
    }
  }, [context, renderMode, isProviderInitialized]);

  return (
    <OpenFeatureProvider {...buildProviderOptions(defaultEvaluationOptions)}>
      {children}
    </OpenFeatureProvider>
  );
}
